import { useEffect } from "react";
import { Loader2, X } from "lucide-react";

export const inputCls =
  "mt-1.5 w-full rounded-[9px] border border-[#DFE6ED] bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-[#147D78]";
export const labelCls = "block text-xs font-semibold text-[#334e68]";

export const Btn = ({ variant = "primary", className = "", children, loading, ...rest }) => {
  const styles = {
    primary: "bg-[#102A43] text-white",
    gold: "bg-[#D9A441] text-[#17212b]",
    ghost: "border border-[#DFE6ED] bg-white text-[#102A43]",
    danger: "border border-[#f4c9c6] bg-[#fdecec] text-[#B42318]",
    teal: "bg-[#147D78] text-white",
  }[variant];
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-[9px] px-4 py-2.5 text-sm font-bold transition-transform active:scale-[0.97] disabled:opacity-60 ${styles} ${className}`}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
};

export const Field = ({ label, hint, children, className = "" }) => (
  <div className={className}>
    <label className={labelCls}>{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-[#8598ac]">{hint}</p>}
  </div>
);

export const Input = (props) => <input {...props} className={`${inputCls} ${props.className || ""}`} />;
export const Textarea = (props) => <textarea rows={4} {...props} className={`${inputCls} ${props.className || ""}`} />;
export const Select = ({ options = [], children, ...rest }) => (
  <select {...rest} className={`${inputCls} ${rest.className || ""}`}>
    {children ||
      options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o || "Any"];
        return (
          <option key={String(v)} value={v}>
            {l}
          </option>
        );
      })}
  </select>
);

export const Card = ({ className = "", children, ...rest }) => (
  <div {...rest} className={`rounded-[14px] border border-[#DFE6ED] bg-white p-5 ${className}`}>
    {children}
  </div>
);

export const SectionTitle = ({ overline, title, sub, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      {overline && (
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">{overline}</p>
      )}
      <h2 className="mt-2 font-display text-2xl font-black tracking-tight text-[#102A43] sm:text-3xl">
        {title}
      </h2>
      {sub && <p className="mt-2 max-w-2xl text-sm text-[#667788] md:text-base">{sub}</p>}
    </div>
    {right}
  </div>
);

export const StatCard = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="rounded-[14px] border border-[#DFE6ED] bg-white p-4">
    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{label}</p>
    <p className="mt-1 font-display text-2xl font-black text-[#102A43]">{value ?? "—"}</p>
    {sub && <p className="mt-0.5 text-xs text-[#8598ac]">{sub}</p>}
  </div>
);

export const Table = ({ head = [], children, testId }) => (
  <div className="mt-4 overflow-x-auto rounded-[14px] border border-[#DFE6ED] bg-white">
    <table data-testid={testId} className="spec-table w-full text-sm">
      <thead>
        <tr className="bg-[#F5F7FA] text-[10px] uppercase tracking-[0.12em] text-[#667788]">
          {head.map((h, i) => (
            <th key={typeof h === "string" ? h : i}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const Pill = ({ tone = "neutral", children, testId }) => {
  const tones = {
    neutral: "bg-[#eef1f5] text-[#48607a] border-[#DFE6ED]",
    ok: "bg-[#e9f5f2] text-[#13795B] border-[#bfe0d8]",
    warn: "bg-[#fff4df] text-[#B7791F] border-[#f0dcb4]",
    bad: "bg-[#fdecec] text-[#B42318] border-[#f4c9c6]",
    gold: "bg-[#fdf4e3] text-[#8a6412] border-[#f0dcb4]",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

export const STATUS_TONE = {
  open: "ok", awarded: "gold", closed: "neutral", cancelled: "bad", in_review: "warn",
  submitted: "ok", revised: "warn", won: "ok", lost: "bad",
  verified: "ok", pending: "warn", rejected: "bad",
  active: "ok", suspended: "bad", expired: "bad",
  managed_review: "warn", managed_accepted: "ok", managed_declined: "bad",
  awarded_direct: "ok", in_progress: "warn", completed: "ok",
};

export const Loading = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 py-14 text-sm text-[#667788]">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);

export const Empty = ({ title, sub, action, testId }) => (
  <div
    data-testid={testId}
    className="mt-4 rounded-[14px] border border-dashed border-[#DFE6ED] bg-white p-10 text-center"
  >
    <p className="font-display text-base font-bold text-[#102A43]">{title}</p>
    {sub && <p className="mx-auto mt-2 max-w-md text-sm text-[#667788]">{sub}</p>}
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </div>
);

export const Modal = ({ title, onClose, children, wide = false }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div
      data-testid="modal"
      className={`w-full rounded-[14px] border border-[#DFE6ED] bg-white p-6 ${wide ? "max-w-4xl" : "max-w-2xl"}`}
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-display text-lg font-bold tracking-tight text-[#102A43]">{title}</h3>
        <button data-testid="modal-close-btn" onClick={onClose} aria-label="Close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
  );
};
